import { useEffect, useState } from 'react';

/**
 * Subscribe to a CSS media query. Returns whether it currently matches and
 * re-renders when that changes. SSR-safe (defaults to false when there is no
 * window). Use for layout branches that must pick one tree to render, e.g.
 * a dedicated mobile component vs. a desktop one.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() =>
    typeof window !== 'undefined' && 'matchMedia' in window ? window.matchMedia(query).matches : false
  );

  useEffect(() => {
    if (typeof window === 'undefined' || !('matchMedia' in window)) return undefined;
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

/** True below the Tailwind `lg` breakpoint (1024px) - i.e. phones and tablets. */
export const useIsMobile = () => useMediaQuery('(max-width: 1023px)');
